import _extends from "@babel/runtime/helpers/esm/extends";
import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _toConsumableArray from "@babel/runtime/helpers/esm/toConsumableArray";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
var _excluded = ["autoMobileMenuHeight", "borderRadius", "clearable", "components", "confirmLabel", "controlProps", "defaultOptions", "desktopMaxMenuHeight", "disableMobileOutsideBlur", "ellipsis", "invalid", "isAutoOptionHeight", "isDisabled", "isOpen", "keepPlaceholder", "loadOptions", "menuListProps", "menuVariant", "mobileMaxMenuHeight", "multiple", "noOptionsMessage", "onChange", "onClickOverlay", "onMenuClose", "openOnFocus", "optionCheckColor", "optionEllipsis", "optionMaxTextLength", "options", "placeholder", "rightMenu", "searchable", "shouldScrollIntoView", "size", "styles", "tagMaxTextLength", "theme", "tooltip", "type", "value", "icon", "namespace", "autoComplete", "tooltipTextProps", "classes", "onMenuOpen", "autoFieldHeight", "menuRef", "isHideWhenOutOfBlock", "getContainer", "onChangeSearch"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import classNames from 'classnames/bind';
import { defaultPropObject } from "../constants";
import { useAutocomplete, useCombinedRefs, useIntersectionObserver, useMediaQuery, useWindowSize } from "../hooks";
import { TextBase } from "../internal/TextBase";
import { canUseDOM } from "../utils/canUseDOM";
import { isIE11 } from "../utils/detectDevice";
import { getMethodsForTestId } from "../utils/getMethodsForTestId";
import { keyCode } from "../utils/keyCodes";
import replaceLatinKeyToCyrillic from "../utils/slugify";
import { truncateText } from "../utils/string";
import { isArray, isString } from "../utils/type";
import { ClearIndicator, Control, DropdownIndicator, formatGroupLabel, Group, GroupHeading, IndicatorsContainer, Input, LoadingMessage, Menu, MenuList, MultiValue, MultiValueContainer, MultiValueLabel, MultiValueRemove, NoOptionsMessage, Option, SelectContainer, SingleValue, ValueContainer } from './components';
import { MAX_DESKTOP_LIST_HEIGHT, MAX_MOBILE_LIST_HEIGHT, MAX_MOBILE_LIST_HEIGHT_FOR_SMALL_SCREEN, MAX_MULTIPLE_MOBILE_LIST_HEIGHT, MOBILE_POPUP_FOOTER_HEIGHT, MOBILE_POPUP_HEADER_HEIGHT, MOBILE_ROW_HEIGHT, style } from './constants';
import getIndexOfSubstring from './getIndexOfSubstring';
import { isOptionGroup } from './guards';
import { checkWhetherFuncReturnNull } from './helpers';
import styles from './Select.css';
var cx = classNames.bind(styles);
var baseComponents = {
  ClearIndicator: ClearIndicator,
  Control: Control,
  DropdownIndicator: DropdownIndicator,
  Group: Group,
  GroupHeading: GroupHeading,
  IndicatorsContainer: IndicatorsContainer,
  Input: Input,
  LoadingMessage: LoadingMessage,
  Menu: Menu,
  MenuList: MenuList,
  MultiValue: MultiValue,
  MultiValueContainer: MultiValueContainer,
  MultiValueLabel: MultiValueLabel,
  MultiValueRemove: MultiValueRemove,
  NoOptionsMessage: NoOptionsMessage,
  Option: Option,
  Placeholder: function Placeholder() {
    return null;
  },
  SelectContainer: SelectContainer,
  SingleValue: SingleValue,
  ValueContainer: ValueContainer,
  IndicatorSeparator: function IndicatorSeparator() {
    return null;
  },
  LoadingIndicator: function LoadingIndicator() {
    return null;
  }
};

var SelectBase = function SelectBase(_ref) {
  var _cx;

  var autoMobileMenuHeight = _ref.autoMobileMenuHeight,
      borderRadius = _ref.borderRadius,
      clearable = _ref.clearable,
      components = _ref.components,
      _ref$confirmLabel = _ref.confirmLabel,
      confirmLabel = _ref$confirmLabel === void 0 ? 'Apply' : _ref$confirmLabel,
      controlProps = _ref.controlProps,
      defaultOptions = _ref.defaultOptions,
      desktopMaxMenuHeight = _ref.desktopMaxMenuHeight,
      disableMobileOutsideBlur = _ref.disableMobileOutsideBlur,
      _ref$ellipsis = _ref.ellipsis,
      ellipsis = _ref$ellipsis === void 0 ? true : _ref$ellipsis,
      invalid = _ref.invalid,
      isAutoOptionHeight = _ref.isAutoOptionHeight,
      isDisabled = _ref.isDisabled,
      isOpen = _ref.isOpen,
      _ref$keepPlaceholder = _ref.keepPlaceholder,
      keepPlaceholderProp = _ref$keepPlaceholder === void 0 ? true : _ref$keepPlaceholder,
      loadOptions = _ref.loadOptions,
      menuListProps = _ref.menuListProps,
      menuVariant = _ref.menuVariant,
      mobileMaxMenuHeight = _ref.mobileMaxMenuHeight,
      _ref$multiple = _ref.multiple,
      multiple = _ref$multiple === void 0 ? false : _ref$multiple,
      _ref$noOptionsMessage = _ref.noOptionsMessage,
      getNoOptionsMessage = _ref$noOptionsMessage === void 0 ? function () {
    return 'No options';
  } : _ref$noOptionsMessage,
      onChange = _ref.onChange,
      onClickOverlay = _ref.onClickOverlay,
      onMenuClose = _ref.onMenuClose,
      openOnFocus = _ref.openOnFocus,
      optionCheckColor = _ref.optionCheckColor,
      _ref$optionEllipsis = _ref.optionEllipsis,
      optionEllipsis = _ref$optionEllipsis === void 0 ? true : _ref$optionEllipsis,
      optionMaxTextLength = _ref.optionMaxTextLength,
      optionsProp = _ref.options,
      _ref$placeholder = _ref.placeholder,
      placeholder = _ref$placeholder === void 0 ? '' : _ref$placeholder,
      rightMenu = _ref.rightMenu,
      _ref$searchable = _ref.searchable,
      searchable = _ref$searchable === void 0 ? false : _ref$searchable,
      shouldScrollIntoView = _ref.shouldScrollIntoView,
      size = _ref.size,
      styleObject = _ref.styles,
      _ref$tagMaxTextLength = _ref.tagMaxTextLength,
      tagMaxTextLength = _ref$tagMaxTextLength === void 0 ? 30 : _ref$tagMaxTextLength,
      _ref$theme = _ref.theme,
      theme = _ref$theme === void 0 ? 'b2c' : _ref$theme,
      tooltip = _ref.tooltip,
      type = _ref.type,
      value = _ref.value,
      icon = _ref.icon,
      namespace = _ref.namespace,
      autoComplete = _ref.autoComplete,
      tooltipTextProps = _ref.tooltipTextProps,
      _ref$classes = _ref.classes,
      classes = _ref$classes === void 0 ? defaultPropObject : _ref$classes,
      onMenuOpen = _ref.onMenuOpen,
      autoFieldHeight = _ref.autoFieldHeight,
      customMenuRef = _ref.menuRef,
      isHideWhenOutOfBlock = _ref.isHideWhenOutOfBlock,
      getContainer = _ref.getContainer,
      onChangeSearch = _ref.onChangeSearch,
      other = _objectWithoutProperties(_ref, _excluded);

  var _useState = useState(true),
      _useState2 = _slicedToArray(_useState, 2),
      isDisplayedSearch = _useState2[0],
      setIsDisplayedSearch = _useState2[1];

  var _useState3 = useState(isOpen || false),
      _useState4 = _slicedToArray(_useState3, 2),
      isOpened = _useState4[0],
      setIsOpened = _useState4[1];

  var _useState5 = useState(''),
      _useState6 = _slicedToArray(_useState5, 2),
      inputValue = _useState6[0],
      setInputValue = _useState6[1];

  var _useState7 = useState(0),
      _useState8 = _slicedToArray(_useState7, 2),
      keyDownCounter = _useState8[0],
      setKeyDownCounter = _useState8[1];

  var optionHeights = useRef(Object.create(null));

  var _useState9 = useState(0),
      _useState10 = _slicedToArray(_useState9, 2),
      countHiddenItems = _useState10[0],
      setCountHiddenItems = _useState10[1];

  var selectRef = useRef(null);
  var inputRef = useRef();
  var rootRef = useRef(null);
  var menuRef = useRef(null);
  var combineMenuRef = useCombinedRefs(menuRef, customMenuRef);

  var _useWindowSize = useWindowSize(),
      height = _useWindowSize.height;

  var breakpoints = useMediaQuery('xs');
  var isMobile = breakpoints.xs;

  var _useAutocomplete = useAutocomplete(inputRef.current),
      isAutofill = _useAutocomplete.isAutofill;

  var _getMethodsForTestId = getMethodsForTestId(namespace, 'select'),
      getTestId = _getMethodsForTestId.getTestId,
      getNamespace = _getMethodsForTestId.getNamespace;

  var keepPlaceholder = useMemo(function () {
    return keepPlaceholderProp && !multiple && placeholder;
  }, [keepPlaceholderProp, multiple, placeholder]);
  var options = useMemo(function () {
    return (optionsProp || []).filter(function (item) {
      return !!item;
    });
  }, [optionsProp]);
  useEffect(function () {
    if (isMobile && !disableMobileOutsideBlur && isOpened && canUseDOM && document.activeElement) {
      document.activeElement.blur();
    }
  }, [disableMobileOutsideBlur, isMobile, isOpened]);
  var hidePlaceholder = !keepPlaceholder && value && !multiple || multiple && isArray(value) && (value === null || value === void 0 ? void 0 : value.length) > 0;
  var isNoOptionsReturnNull = checkWhetherFuncReturnNull(getNoOptionsMessage);
  var isGroup = useMemo(function () {
    return options.some(function (item) {
      if (isOptionGroup(item)) {
        return item === null || item === void 0 ? void 0 : item.options;
      }

      return false;
    });
  }, [options]);
  var noOptionsMessage = useMemo(function () {
    if (isMobile && isNoOptionsReturnNull) {
      return function () {
        return '';
      };
    }

    return getNoOptionsMessage;
  }, [isMobile, getNoOptionsMessage, isNoOptionsReturnNull]);
  var handleMenuOpen = useCallback(function () {
    setIsOpened(true);
  }, []);
  var handleMenuClose = useCallback(function () {
    setIsOpened(false);
  }, []);

  var handleChange = function handleChange(val) {
    var newValue = val;

    if (multiple && tagMaxTextLength && isArray(val)) {
      newValue = val.reduce(function (acc, item) {
        return [].concat(_toConsumableArray(acc), [_objectSpread(_objectSpread({}, item), {}, {
          label: truncateText(item.label, tagMaxTextLength)
        })]);
      }, []);
    }

    if (!multiple) {
      handleMenuClose();
    }

    onChange(newValue !== null ? newValue : []);
  };

  var handleMenuToggle = useCallback(function () {
    setIsOpened(!isOpened);
  }, [isOpened]);

  var handleKeyDown = function handleKeyDown(e) {
    if (searchable && !multiple && !isMobile && value && e.keyCode === keyCode.BACKSPACE) {
      onChange(null);
    }

    if (e.keyCode === keyCode.UP || e.keyCode === keyCode.DOWN) {
      setKeyDownCounter(keyDownCounter + 1);
    }
  };

  var handleInputChange = function handleInputChange(newValue, params) {
    if (params.action === 'input-change' && onChangeSearch) {
      onChangeSearch(newValue);
    }

    if (multiple && params && params.action === 'set-value') {
      setInputValue(inputValue);
      return inputValue;
    }

    setInputValue(newValue);
    return newValue;
  };

  var handleFilterOption = useCallback(function (items, currentValue) {
    if (loadOptions || !isString(items.data.label) && !isString(items.data.name)) {
      return true;
    }

    var label = items.data.name || items.data.code ? "".concat(items.data.name).concat(items.data.code).concat(items.data.label) : items.data.label;
    return getIndexOfSubstring(label, currentValue || '') >= 0 || getIndexOfSubstring(label, replaceLatinKeyToCyrillic(currentValue) || '') >= 0;
  }, [loadOptions]);
  /*
  Так как useIntersectionObserver в callback передает только элементы изменившие
  свое состояние, используем хак для подсчета количества скрытых через dataset.hidden
  */

  var calcCountHiddenItems = useCallback(function (obser) {
    return function (entries) {
      var _obser$current, _obser$current$root;

      if (entries) {
        entries.forEach(function (entry) {
          if (entry.target instanceof HTMLElement) {
            // eslint-disable-next-line no-param-reassign
            entry.target.dataset.hidden = String(!entry.isIntersecting);
          }
        });
      }

      var hiddenItems = _toConsumableArray(Array.from((obser === null || obser === void 0 ? void 0 : (_obser$current = obser.current) === null || _obser$current === void 0 ? void 0 : (_obser$current$root = _obser$current.root) === null || _obser$current$root === void 0 ? void 0 : _obser$current$root.childNodes) || []) || []).reduce(function (prev, target) {
        if (target instanceof HTMLElement) {
          return target.dataset.hidden === 'true' ? prev + 1 : prev;
        }

        return prev;
      }, 0);

      setCountHiddenItems(hiddenItems); // eslint-disable-next-line react-hooks/exhaustive-deps
    };
  }, []);

  var _useIntersectionObser = useIntersectionObserver({
    threshold: 0.9,
    cb: calcCountHiddenItems()
  }),
      observer = _useIntersectionObser.observer,
      setRef = _useIntersectionObser.setRef;

  var Component = loadOptions ? AsyncSelect : Select;
  var calculatedMobileMenuHeight = useMemo(function () {
    if (autoMobileMenuHeight) {
      var result = height - MOBILE_POPUP_HEADER_HEIGHT;

      if (searchable && isDisplayedSearch) {
        result -= MOBILE_ROW_HEIGHT;
      }

      if (multiple) {
        result -= MOBILE_POPUP_FOOTER_HEIGHT;
      }

      return result < MOBILE_ROW_HEIGHT ? result + MOBILE_POPUP_HEADER_HEIGHT : result;
    }

    if (mobileMaxMenuHeight) return mobileMaxMenuHeight;
    if (searchable && height < 600) return MAX_MOBILE_LIST_HEIGHT_FOR_SMALL_SCREEN;
    if (searchable) return height - MOBILE_ROW_HEIGHT;
    return multiple ? MAX_MULTIPLE_MOBILE_LIST_HEIGHT : MAX_MOBILE_LIST_HEIGHT;
  }, [autoMobileMenuHeight, height, isDisplayedSearch, mobileMaxMenuHeight, multiple, searchable]); // const setFocusFirstOption = (_: any, findOptions: Options<OptionSelect>) =>
  //   value || findOptions[0];
  // По умолчанию фокус ставится на элемент который находится первым в списке при наборе текста, если текст стереть или отфильтровать налету, то фокус может располагаться не на первом элементе
  // Функционал переводит фокус на первый элемент в списке
  // https://github.com/JedWatson/react-select/pull/4080

  useEffect(function () {
    if (selectRef.current) {
      if (loadOptions) {// eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        // selectRef.current?.select?.select?.getNextFocusedOption = setFocusFirstOption;
      } else {// eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        // selectRef.current?.select?.getNextFocusedOption = setFocusFirstOption;
      }
    }

    calcCountHiddenItems(observer)(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
  var container = typeof getContainer === 'function' ? getContainer() : undefined;
  return /*#__PURE__*/React.createElement("div", _extends({
    ref: rootRef,
    className: cx('root', {
      root_invalid: invalid
    }, classes.root)
  }, getTestId()), /*#__PURE__*/React.createElement(Component, _extends({
    captureMenuScroll: isIE11 ? false : undefined,
    className: cx('Select', (_cx = {}, _defineProperty(_cx, "Select_type_".concat(type), type), _defineProperty(_cx, "Select_theme_".concat(theme), theme), _defineProperty(_cx, "Select_size_".concat(size), size), _defineProperty(_cx, "Select_rightMenu", rightMenu), _defineProperty(_cx, "searchable", searchable), _defineProperty(_cx, "keepPlaceholder", keepPlaceholder && !multiple), _defineProperty(_cx, "hidePlaceholder", hidePlaceholder), _defineProperty(_cx, "isAutoOptionHeight", isAutoOptionHeight), _defineProperty(_cx, "hasValue", Boolean(value)), _cx), classes.select),
    closeMenuOnSelect: isMobile ? false : !multiple,
    hideSelectedOptions: false,
    isClearable: clearable,
    isMulti: multiple,
    isSearchable: searchable,
    onKeyDown: handleKeyDown,
    onInputChange: handleInputChange,
    ref: selectRef,
    filterOption: handleFilterOption // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    ,
    styles: _objectSpread(_objectSpread({}, style), styleObject),
    tabSelectsValue: false,
    borderRadius: borderRadius,
    foc: true,
    blurInputOnSelect: other.autoSelectAloneValue,
    colorTheme: theme // Компоненты из папки ./components не затипизированы
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    ,
    components: _objectSpread(_objectSpread({}, baseComponents), components),
    confirmLabel: confirmLabel,
    controlProps: controlProps,
    ellipsis: ellipsis,
    formatGroupLabel: formatGroupLabel,
    handleMenuClose: handleMenuClose,
    handleMenuOpen: handleMenuOpen,
    handleMenuToggle: handleMenuToggle,
    inputRef: inputRef,
    isAutoOptionHeight: isAutoOptionHeight,
    isDisabled: isDisabled,
    isDisplayedSearch: isDisplayedSearch,
    isGroup: isGroup,
    isMobile: isMobile,
    isNoOptionsReturnNull: isNoOptionsReturnNull,
    keepPlaceholder: keepPlaceholder,
    keyDownCounter: keyDownCounter,
    menuListProps: menuListProps,
    menuVariant: menuVariant // TODO remove
    ,
    noOptionsMessage: noOptionsMessage,
    onChange: handleChange,
    onClickOverlay: onClickOverlay,
    optionCheckColor: optionCheckColor,
    optionEllipsis: optionEllipsis,
    optionHeights: optionHeights.current,
    optionMaxTextLength: optionMaxTextLength,
    placeholder: placeholder,
    selectRef: selectRef.current,
    rootRef: rootRef,
    setIsDisplayedSearch: setIsDisplayedSearch,
    shouldScrollIntoView: shouldScrollIntoView,
    value: value,
    autoMobileMenuHeight: autoMobileMenuHeight,
    icon: icon,
    namespace: getNamespace(),
    autoComplete: autoComplete,
    isAutofill: isAutofill,
    classes: classes,
    valueContainerRef: setRef,
    countHiddenItems: countHiddenItems,
    observerHiddenItems: observer,
    onMenuOpen: onMenuOpen,
    onMenuClose: onMenuClose,
    autoFieldHeight: autoFieldHeight,
    menuRef: combineMenuRef,
    isHideWhenOutOfBlock: isHideWhenOutOfBlock,
    container: container
  }, isMobile ? _objectSpread({
    backspaceRemovesValue: false,
    maxMenuHeight: calculatedMobileMenuHeight,
    menuIsOpen: isOpened,
    minMenuHeight: calculatedMobileMenuHeight
  }, loadOptions ? {
    onLoadOptions: loadOptions
  } : {}) : {
    maxMenuHeight: desktopMaxMenuHeight || MAX_DESKTOP_LIST_HEIGHT,
    openMenuOnFocus: openOnFocus
  }, loadOptions ? {
    loadOptions: loadOptions,
    defaultOptions: defaultOptions,
    cacheOptions: true
  } : {
    options: options
  }, other)), tooltip && /*#__PURE__*/React.createElement("div", {
    className: cx('tooltip', classes.tooltip)
  }, /*#__PURE__*/React.createElement(TextBase, _extends({}, tooltipTextProps, {
    as: "span",
    color: invalid ? 'R600' : 'N100',
    size: "s"
  }), tooltip)));
};

export default SelectBase;